import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { fuseAnimations } from '@fuse/animations';
import { CreateFileUploadEntryInput } from 'app/api/API.service';
import { AccountingYear, CompanySubset } from 'app/api/company.api.service';
import { FileUploadAPIService, FileUploadEntry } from 'app/api/file-upload.api.service';
import * as _ from 'lodash';
import * as moment from 'moment';
import { FileUploadService } from '../file-upload.service';
import { parseErrorMessage } from 'app/utils/callstate';

export interface MonthItem {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'file-upload-dialog',
  templateUrl: './file-upload-dialog.component.html',
  styleUrls: ['./file-upload-dialog.component.scss'],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None
})
export class FileUploadDialogComponent {
  fileUploadForm: FormGroup;
  startMonths: MonthItem[];
  untilMonths: MonthItem[];
  errorMessage: string;
  uploadResponse = { status: '', message: '', filePath: '' };
  isImporting = false;

  constructor(
    public dialogRef: MatDialogRef<FileUploadDialogComponent>,
    public fileUploadAPIService: FileUploadAPIService,
    public fileUploadService: FileUploadService,
    @Inject(MAT_DIALOG_DATA) public data: { company: CompanySubset },
    private _formBuilder: FormBuilder
  ) {
    if (data.company.accountingYears) {
      this.startMonths = this.loadStartMonths(data.company.accountingYears);
    }

    this.untilMonths = [];
    this.fileUploadForm = this.createFileUploadForm(data.company);
  }

  createFileUploadForm(company: CompanySubset): FormGroup {
    const startDate = '';
    const untilDateExcluded = '';
    const fileType = 'accounting';
    const sourceFile = '';
    const closedAccounting = false;

    return new FormGroup({
      companyId: new FormControl(company.id),
      startDate: new FormControl(startDate),
      untilDateExcluded: new FormControl(untilDateExcluded),
      fileType: new FormControl(fileType),
      sourceFile: new FormControl(sourceFile, Validators.required),
      closedAccounting: new FormControl(closedAccounting),
    });
  }

  loadStartMonths(accountingYears: AccountingYear[]): MonthItem[] {
    return _.map(accountingYears, accountingYear => {
      return {
        value: accountingYear.startDate,
        viewValue: moment.utc(accountingYear.startDate).format('MMMM YYYY')
      };
    });
  }

  loadUntilMonths(startDate: string): MonthItem[] {
    const start = moment.utc(startDate).add(1, 'month').startOf('month');
    const end = moment.utc().startOf('month');
    const months: MonthItem[] = [];
    while (end >= start) {
      const viewValue = start.clone().add(-1, 'month').format('MMMM YYYY');
      const value = start.toISOString();
      months.push({ value, viewValue });
      start.add(1, 'month');
    }
    return months;
  }

  onStartDateChange(event): void {
    const accountingYear = _.find(this.data.company.accountingYears, ay => ay.startDate === event.value.value);
    if (accountingYear) {
      this.untilMonths = this.loadUntilMonths(accountingYear.startDate);
      this.fileUploadForm.controls['closedAccounting'].setValue(!!accountingYear.closedAccounting);
      if (!!accountingYear.closedAccounting) {
        this.fileUploadForm.controls['closedAccounting'].disable();
        const untilMonth = this.untilMonths.find(um => um.value === moment.utc(accountingYear.endDate).toISOString());
        this.fileUploadForm.controls['untilDateExcluded'].setValue(untilMonth);
      } else {
        this.fileUploadForm.controls['closedAccounting'].enable();
        const untilMonth = this.untilMonths.find(um => um.value === moment.utc().startOf('month').toISOString());
        this.fileUploadForm.controls['untilDateExcluded'].setValue(untilMonth);
      }
    }
  }

  submit(fileUploadForm: FormGroup): void {
    this.errorMessage = '';
    if (!fileUploadForm.value.sourceFile.files) {
      return;
    }

    const values = fileUploadForm.getRawValue();
    const fileType: string = values.fileType;
    const sourceFile = values.sourceFile;
    const sourceFilename: string = sourceFile.files[0].name;
    const contentType: string = sourceFile.files[0].type;
    const input: CreateFileUploadEntryInput = {
      companyId: values.companyId,
      fileType,
      sourceFilename
    };
    if (fileType === 'accounting') {
      input.startDate = values.startDate.value;
      input.untilDateExcluded = values.untilDateExcluded.value;
      input.closedAccounting = values.closedAccounting;
    }
/*    if (fileType === 'payroll') {
      input.startDate = values.startDate.value;
    }*/

    this.isImporting = true;
    this.fileUploadAPIService
      .CreateFileUploadEntry(input)
      .toPromise()
      .then((entry: FileUploadEntry) => {
        this.fileUploadService
          .upload(sourceFile.files[0], entry.s3SignedURL)
          .subscribe(
            (res: { status: string; message: string; filePath: string; }) => {
              if (res.status === 'progress') {
                this.uploadResponse = res;
              } else if (res.status === 'done') {
                this.isImporting = false;
                this.dialogRef.close(res);
              }
            },
            (err) => {
              this.errorMessage = err;
              this.isImporting = false;
            }
          );

      })
      .catch(err => {
        this.errorMessage = parseErrorMessage(err);
        this.isImporting = false;
        console.error('Failed to create upload entry', this.errorMessage);
      });

  }

}
